<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { formatDistanceToNow, fromUnixTime } from 'date-fns';

import Spinner from 'shared/components/Spinner.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const operations = computed(
  () => getters['labelContactBulkOperations/getOperations'].value
);
const uiFlags = computed(
  () => getters['labelContactBulkOperations/getUIFlags'].value
);

onMounted(() => {
  store.dispatch('labelContactBulkOperations/fetch');
});

const statusClasses = {
  queued: 'bg-n-slate-3 text-n-slate-11',
  running: 'bg-n-blue-3 text-n-blue-11',
  completed: 'bg-n-teal-3 text-n-teal-11',
  failed: 'bg-n-ruby-3 text-n-ruby-11',
};

const statusBadgeClass = status =>
  statusClasses[status] || statusClasses.queued;

const progressPercentage = op => {
  const total = Number(op.total_contacts) || 0;
  if (total === 0) return 0;
  return Math.round((Number(op.processed_contacts) / total) * 100);
};

const formatTime = unixOrNull => {
  if (!unixOrNull) return null;
  try {
    const date =
      typeof unixOrNull === 'number'
        ? fromUnixTime(unixOrNull)
        : new Date(unixOrNull);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return null;
  }
};

const actionLabel = action => {
  if (action === 'apply_to_contacts')
    return t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.ACTION.APPLY_TO_CONTACTS');
  if (action === 'remove_from_contacts')
    return t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.ACTION.REMOVE_FROM_CONTACTS');
  return action;
};

const statusLabel = status => {
  const key = status?.toUpperCase();
  return key ? t(`LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.STATUS.${key}`) : status;
};

const primaryTimeLabel = op => {
  if (op.completed_at) {
    return t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.TIMING.COMPLETED', {
      when: formatTime(op.completed_at),
    });
  }
  if (op.started_at) {
    return t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.TIMING.STARTED', {
      when: formatTime(op.started_at),
    });
  }
  if (op.created_at) {
    return t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.TIMING.CREATED', {
      when: formatTime(op.created_at),
    });
  }
  return '';
};
</script>

<template>
  <div class="flex flex-col w-full">
    <div
      v-if="uiFlags.isFetching && operations.length === 0"
      class="flex items-center gap-2 py-12 text-sm text-n-slate-11"
    >
      <Spinner size="small" />
      <span>{{ $t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.LOADING') }}</span>
    </div>

    <p
      v-else-if="operations.length === 0"
      class="py-12 text-center text-sm text-n-slate-11"
    >
      {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.EMPTY') }}
    </p>

    <table v-else class="min-w-full divide-y divide-n-weak">
      <thead>
        <tr>
          <th
            class="py-4 pr-4 text-xs font-semibold text-left uppercase text-n-slate-11"
          >
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.COLUMNS.LABEL') }}
          </th>
          <th
            class="py-4 pr-4 text-xs font-semibold text-left uppercase text-n-slate-11"
          >
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.COLUMNS.ACTION') }}
          </th>
          <th
            class="py-4 pr-4 text-xs font-semibold text-left uppercase text-n-slate-11"
          >
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.COLUMNS.STATUS') }}
          </th>
          <th
            class="py-4 pr-4 text-xs font-semibold text-left uppercase text-n-slate-11"
          >
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.COLUMNS.PROGRESS') }}
          </th>
          <th
            class="py-4 pr-4 text-xs font-semibold text-left uppercase text-n-slate-11"
          >
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.COLUMNS.RESULT') }}
          </th>
          <th
            class="py-4 pr-4 text-xs font-semibold text-left uppercase text-n-slate-11"
          >
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.COLUMNS.CREATED_BY') }}
          </th>
          <th
            class="py-4 pr-4 text-xs font-semibold text-left uppercase text-n-slate-11"
          >
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.COLUMNS.TIMING') }}
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak text-n-slate-12">
        <tr v-for="op in operations" :key="op.id">
          <td class="py-4 pr-4 font-medium">{{ op.label_title }}</td>
          <td class="py-4 pr-4">{{ actionLabel(op.action) }}</td>
          <td class="py-4 pr-4">
            <span
              class="inline-flex px-2 py-1 text-xs font-medium rounded-full"
              :class="statusBadgeClass(op.status)"
            >
              {{ statusLabel(op.status) }}
            </span>
          </td>
          <td class="py-4 pr-4 min-w-[160px]">
            <div class="flex flex-col gap-1">
              <div
                class="flex items-center w-full h-1.5 overflow-hidden rounded-full bg-n-alpha-2"
              >
                <div
                  class="h-full transition-all duration-300 rounded-full bg-n-brand"
                  :style="{ width: `${progressPercentage(op)}%` }"
                />
              </div>
              <span class="text-xs text-n-slate-11">
                {{ op.processed_contacts || 0 }} /
                {{ op.total_contacts || 0 }} ({{ progressPercentage(op) }}%)
              </span>
            </div>
          </td>
          <td class="py-4 pr-4 text-xs">
            <div class="flex flex-col gap-0.5">
              <span class="text-n-teal-11">
                {{
                  $t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.RESULT.UPDATED', {
                    count: op.updated_contacts || 0,
                  })
                }}
              </span>
              <span class="text-n-slate-11">
                {{
                  $t('LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.RESULT.SKIPPED', {
                    count: op.skipped_contacts || 0,
                  })
                }}
              </span>
              <span v-if="op.failed_contacts > 0" class="text-n-ruby-11">
                {{
                  $t(
                    'LABEL_MGMT.BULK_CONTACT_OPS.HISTORY.RESULT.FAILED_COUNT',
                    { count: op.failed_contacts }
                  )
                }}
              </span>
            </div>
          </td>
          <td class="py-4 pr-4 text-sm text-n-slate-11">
            <span v-if="op.created_by?.name">{{ op.created_by.name }}</span>
          </td>
          <td class="py-4 pr-4 text-xs text-n-slate-11">
            {{ primaryTimeLabel(op) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
